import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAssignManager, useCreateEmployeeAdvanceAccount, useSearchAccounts, useSearchCostCenters, useSearchEmployees, useSetApprover, useSetEmployeeAdvanceAccount } from "../lib/api";
import { ErrorBox } from "./ui";

type ApproverKind = "leave" | "expense" | "timesheet";

type Props = {
  employee: string;
  reportsTo: string | null;
  leaveApprover: string | null;
  expenseApprover: string | null;
  timesheetApprover: string | null;
  advanceAccount: string | null;
  /** Employee id -> display name (for the manager row); built once from the already-loaded employee list. */
  employeeName: (employeeId: string | null) => string | null;
  /** User id -> display name (for the three approver rows, which store a User, not an Employee). */
  approverName: (userId: string | null) => string | null;
};

/** One row: current value + a "change" search-and-pick control. Shared shape for the manager row and the three
 * approver rows so the panel stays compact. */
function Row({ label, currentLabel, employee, onPick }: { label: string; currentLabel: string | null; employee: string; onPick: (candidate: string) => void }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState("");
  const search = useSearchEmployees(q, employee, editing);

  return (
    <div className="flex items-center justify-between gap-2 py-1" data-manager-row={label}>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-brand-grey">{label}</div>
        <div className="truncate text-sm font-medium">{currentLabel ?? t("ppl.manager.none")}</div>
      </div>
      {editing ? (
        <div className="flex items-center gap-1">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ppl.manager.searchPlaceholder")}
            className="w-40 rounded border border-brand-grey-light px-2 py-1 text-xs" data-manager-search />
          <select onChange={(e) => { if (e.target.value) { onPick(e.target.value); setEditing(false); setQ(""); } }} defaultValue=""
            className="w-40 rounded border border-brand-grey-light px-1 py-1 text-xs" data-manager-select>
            <option value="" disabled>{search.isLoading ? t("common.loading") : t("ppl.account.choose")}</option>
            {(search.data ?? []).map((c) => <option key={c.name} value={c.name}>{c.employee_name}</option>)}
          </select>
          <button onClick={() => { setEditing(false); setQ(""); }} className="text-xs text-brand-grey hover:text-brand-charcoal">{t("common.cancel")}</button>
        </div>
      ) : (
        <button onClick={() => setEditing(true)} className="shrink-0 rounded border border-brand-primary px-2 py-0.5 text-xs font-semibold text-brand-primary hover:bg-blue-50" data-manager-change={label}>
          {t("ppl.manager.change")}
        </button>
      )}
    </div>
  );
}

/** Same shell as Row, but the picker searches Accounts (the employee's own company) instead of Employees — for
 * the expense / advance ledger account. Also offers "Create new": a brand-new, dedicated ledger account for just
 * this employee (named Employee code + Cost center + assignment date + a unique 5-digit series), rather than
 * picking one from an existing pooled list. */
function AccountRow({ label, currentLabel, employee, onPick, onCreate }: { label: string; currentLabel: string | null; employee: string; onPick: (account: string) => void; onCreate: (costCenter?: string) => void }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [ccQuery, setCcQuery] = useState("");
  const [cc, setCc] = useState("");
  const search = useSearchAccounts(employee, q, editing);
  const ccSearch = useSearchCostCenters(employee, ccQuery, creating);

  const resetAll = () => { setEditing(false); setCreating(false); setQ(""); setCcQuery(""); setCc(""); };

  return (
    <div className="flex items-center justify-between gap-2 py-1" data-manager-row={label}>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-brand-grey">{label}</div>
        <div className="truncate text-sm font-medium">{currentLabel ?? t("ppl.manager.none")}</div>
      </div>
      {editing ? (
        <div className="flex items-center gap-1">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ppl.manager.accountSearchPlaceholder")}
            className="w-40 rounded border border-brand-grey-light px-2 py-1 text-xs" data-manager-search />
          <select onChange={(e) => { if (e.target.value) { onPick(e.target.value); resetAll(); } }} defaultValue=""
            className="w-40 rounded border border-brand-grey-light px-1 py-1 text-xs" data-manager-select>
            <option value="" disabled>{search.isLoading ? t("common.loading") : t("ppl.account.choose")}</option>
            {(search.data ?? []).map((c) => <option key={c.name} value={c.name}>{c.account_name}</option>)}
          </select>
          <button onClick={resetAll} className="text-xs text-brand-grey hover:text-brand-charcoal">{t("common.cancel")}</button>
        </div>
      ) : creating ? (
        <div className="flex flex-wrap items-center justify-end gap-1">
          <input value={ccQuery} onChange={(e) => setCcQuery(e.target.value)} placeholder={t("ppl.manager.costCenterSearchPlaceholder")}
            className="w-36 rounded border border-brand-grey-light px-2 py-1 text-xs" data-manager-cost-center-search />
          <select value={cc} onChange={(e) => setCc(e.target.value)} className="w-36 rounded border border-brand-grey-light px-1 py-1 text-xs" data-manager-cost-center-select>
            <option value="">{ccSearch.isLoading ? t("common.loading") : t("ppl.manager.costCenterAuto")}</option>
            {(ccSearch.data ?? []).map((c) => <option key={c.name} value={c.name}>{c.cost_center_name}</option>)}
          </select>
          <button onClick={() => { onCreate(cc || undefined); resetAll(); }} className="shrink-0 rounded border border-brand-primary px-2 py-0.5 text-xs font-semibold text-brand-primary hover:bg-blue-50" data-manager-create-confirm={label}>
            {t("ppl.manager.createAccount")}
          </button>
          <button onClick={resetAll} className="text-xs text-brand-grey hover:text-brand-charcoal">{t("common.cancel")}</button>
        </div>
      ) : (
        <div className="flex shrink-0 items-center gap-1">
          <button onClick={() => setEditing(true)} className="rounded border border-brand-primary px-2 py-0.5 text-xs font-semibold text-brand-primary hover:bg-blue-50" data-manager-change={label}>
            {t("ppl.manager.change")}
          </button>
          <button onClick={() => setCreating(true)} className="rounded border border-brand-grey-light px-2 py-0.5 text-xs font-semibold text-brand-charcoal hover:bg-[#F6F8FA]" data-manager-create={label}>
            {t("ppl.manager.createNew")}
          </button>
        </div>
      )}
    </div>
  );
}

/** Manager assignment, its three approvers (leave / expense / timesheet, each defaulting to the line manager but
 * independently overridable) and the expense / advance ledger account — shared between the Employee list and
 * Administration. */
export function ManagerAssignment({ employee, reportsTo, leaveApprover, expenseApprover, timesheetApprover, advanceAccount, employeeName, approverName }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const assignManager = useAssignManager();
  const setApprover = useSetApprover();
  const setAdvanceAccount = useSetEmployeeAdvanceAccount();
  const createAdvanceAccount = useCreateEmployeeAdvanceAccount();

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-left text-xs hover:underline" data-manager-open={employee}>
        {reportsTo ? <span className="font-medium">{employeeName(reportsTo) ?? reportsTo}</span> : <span className="text-brand-grey">{t("ppl.manager.assign")}</span>}
      </button>
    );
  }

  const APPROVER_ROWS: { kind: ApproverKind; label: string; value: string | null }[] = [
    { kind: "leave", label: t("ppl.manager.leaveApprover"), value: leaveApprover },
    { kind: "expense", label: t("ppl.manager.expenseApprover"), value: expenseApprover },
    { kind: "timesheet", label: t("ppl.manager.timesheetApprover"), value: timesheetApprover },
  ];

  return (
    <div className="w-72 rounded border border-brand-grey-light bg-white p-2 shadow-sm" data-manager-panel={employee}>
      <Row label={t("ppl.manager.manager")} currentLabel={reportsTo ? employeeName(reportsTo) : null} employee={employee}
        onPick={(candidate) => assignManager.mutate({ employee, manager: candidate })} />
      <div className="my-1 border-t border-brand-grey-light" />
      {APPROVER_ROWS.map((r) => (
        <Row key={r.kind} label={r.label} currentLabel={approverName(r.value)} employee={employee}
          onPick={(candidate) => setApprover.mutate({ employee, kind: r.kind, approver_employee: candidate })} />
      ))}
      <div className="my-1 border-t border-brand-grey-light" />
      <AccountRow label={t("ppl.manager.advanceAccount")} currentLabel={advanceAccount} employee={employee}
        onPick={(account) => setAdvanceAccount.mutate({ employee, account })}
        onCreate={(cost_center) => createAdvanceAccount.mutate({ employee, ...(cost_center ? { cost_center } : {}) })} />
      {(assignManager.isError || setApprover.isError || setAdvanceAccount.isError || createAdvanceAccount.isError) && (
        <div className="mt-1"><ErrorBox error={assignManager.error ?? setApprover.error ?? setAdvanceAccount.error ?? createAdvanceAccount.error} /></div>
      )}
      <div className="mt-1 text-right">
        <button onClick={() => setOpen(false)} className="text-xs text-brand-grey hover:text-brand-charcoal">{t("common.close")}</button>
      </div>
    </div>
  );
}
